import React from 'react';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook } from 'lucide-react';
import { companyInfo, openingHours } from '../data/mock';

// Custom TikTok icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const Contact = () => {
  const contactItems = [
    {
      icon: Phone,
      label: 'Téléphone',
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone.replace(/\s/g, '')}`
    },
    {
      icon: Mail,
      label: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`
    },
    {
      icon: MapPin,
      label: 'Zone d\'intervention',
      value: companyInfo.location,
      cities: companyInfo.serviceArea,
      href: null
    }
  ];

  const availableDays = openingHours
    .filter((d) => d.open)
    .map((d) => `${d.label}: ${d.value}`);

  return (
    <section id="contact" className="section-padding bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50/50 via-transparent to-orange-50/50" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            Contact
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Besoin d'un{' '}
            <span className="text-gradient">renseignement ?</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            N'hésitez pas à nous contacter pour toute question ou demande de devis
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Contact Cards */}
          {contactItems.map((item, index) => {
            const IconComponent = item.icon;
            const content = (
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(207,90%,54%)] to-[hsl(207,90%,44%)] flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.label}</h3>
                <p className="text-gray-600 group-hover:text-[hsl(207,90%,54%)] transition-colors">
                  {item.value}
                </p>
                {item.cities && (
                  <div className="flex flex-wrap gap-1.5 mt-4" data-testid="service-area-cities">
                    {item.cities.map((city) => (
                      <span
                        key={city}
                        className="text-[11px] font-medium bg-blue-50 text-[hsl(207,90%,44%)] px-2.5 py-1 rounded-full border border-blue-100"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );

            return item.href ? (
              <a key={index} href={item.href} className="block">
                {content}
              </a>
            ) : (
              <div key={index}>{content}</div>
            );
          })}
        </div>

        {/* Hours & Social */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          {/* Hours */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[hsl(207,90%,54%)] flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Horaires d'ouverture</h3>
            </div>
            <ul className="space-y-3">
              {openingHours.map((d) => (
                <li key={d.label} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                  <span className="font-medium text-gray-700">{d.label}</span>
                  <span className={d.open ? 'text-[hsl(207,90%,54%)] font-semibold' : 'text-gray-400'}>
                    {d.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media */}
          <div className="bg-gradient-to-br from-orange-50/50 to-slate-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Suivez-nous</h3>
            <p className="text-gray-600 mb-8">
              Retrouvez nos réalisations et conseils sur nos réseaux sociaux
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={`https://instagram.com/${companyInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram-link"
                className="flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Instagram</p>
                  <p className="font-semibold text-gray-900 group-hover:text-[hsl(207,90%,54%)] transition-colors">@{companyInfo.instagram}</p>
                </div>
              </a>
              <a
                href={companyInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook-link"
                className="flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#1877F2] flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-white" fill="currentColor" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Facebook</p>
                  <p className="font-semibold text-gray-900 group-hover:text-[hsl(207,90%,54%)] transition-colors">{companyInfo.facebook}</p>
                </div>
              </a>
              <a
                href={`https://tiktok.com/@${companyInfo.tiktok}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tiktok-link"
                className="flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                  <TikTokIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">TikTok</p>
                  <p className="font-semibold text-gray-900 group-hover:text-[hsl(207,90%,54%)] transition-colors">@{companyInfo.tiktok}</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
